/* ===================================================================
   PGC FEATURE ROW MEDIA  --  shared video controller
   -------------------------------------------------------------------
   ONE controller for every Feature Row on the page. The Why Tallmade
   page renders TWELVE separate section files
   (why-tallmade-feature-row-1 .. -12), each exactly once, and the
   whole point of this file is that it sees all twelve at the same
   time. A copy of this logic inside each section file could only ever
   see its own single video, so the pool below would never trim
   anything -- twelve independent controllers reproduce the exact bug
   they are meant to fix.

   It also means there is only ONE file to update. Nothing here can
   drift out of sync with eleven stale copies.

   -------------------------------------------------------------------
   THE PROBLEM IT SOLVES

   A phone has a small fixed number of hardware video decoders. Twelve
   rows all doing `autoplay muted loop` ask for one each, at once. iOS
   and Android Chrome do not queue the surplus -- they refuse it. The
   element then paints its own background instead of a frame (reported
   as "black spaces"), and once the OS has stalled a video nothing
   restarts it (reported as "not infinite loop"). They also all pull
   bytes simultaneously, which stutters whichever ones do play
   (reported as "black flickers"). Desktop has decoders and bandwidth
   to spare, which is why the same page looks fine on one machine and
   broken on someone's phone.

   -------------------------------------------------------------------
   HOW IT WORKS

   1. LAZY.   Sections ship no `src` at all, only `data-frm-src`.
              Nothing downloads or decodes until asked.
   2. PLAY WHAT IS ON SCREEN. Two observers: one attaches `src` ahead
              of arrival, one plays on enter and pauses on exit.
   3. POOL.   Pausing stops decoding but the element still HOLDS its
              media resource -- an AVPlayer instance on iOS -- and
              there is a hard per-page cap. Scroll through twelve rows
              and you hit it anyway. So at most MAX_ATTACHED rows keep
              a source; over that, the row furthest from the viewport
              hands its source back. A visible row is never released.
   4. RESTART. `loop` is not dependable. Four things restart a video:
              `ended` (iOS can freeze on the last frame), a watchdog
              (currentTime stopped advancing), `visibilitychange` (a
              backgrounded tab pauses and never resumes), and the
              first real gesture (iOS Low Power Mode refuses autoplay
              outright and play() returns a rejected promise).

   -------------------------------------------------------------------
   CONTRACT WITH A SECTION -- four things, no prefixes

     <video data-frm-src="<desktop url>"          (required)
            data-frm-src-mobile="<phone url>"     (optional)
            poster="<first frame>"                (optional)
            preload="none" autoplay muted loop playsinline>

   Attribute names are deliberately NOT per-row (no wfr1-/wfr2-), so
   one controller serves every row and any future section can opt in
   by using the same attributes.
   =================================================================== */
(function () {
  'use strict';

  /* Installed once. Eleven of the twelve script tags land here. */
  if (window.PgcFeatureRowMedia) { return; }

  var SELECTOR = 'video[data-frm-src], video[data-frm-src-mobile]';
  var MOBILE_QUERY = '(max-width: 749px)';
  var PREPARE_MARGIN = '400px 0px';
  /* currentTime on a playing video is a live clock, so at 500ms a
     healthy row always reads as having moved -- there are no false
     positives to trade against. The first tick only sets a baseline,
     so a genuine freeze is caught in about 2.5s. */
  var TICK_MS = 500;
  var STALL_TICKS = 4;
  /* How many rows may hold a media resource at once. Twelve rows, and
     other sections on this page have media of their own, so this is
     deliberately below what the page would otherwise ask for. */
  var MAX_ATTACHED = 4;

  var videos = [];
  var timer = null;
  var prepareIO = null;
  var playIO = null;
  var sawCallback = false;
  var manual = false;
  var manualPending = false;

  function isMobile() {
    return !!(window.matchMedia && window.matchMedia(MOBILE_QUERY).matches);
  }

  function chooseSrc(v) {
    var desktop = v.getAttribute('data-frm-src');
    var mobile = v.getAttribute('data-frm-src-mobile');
    if (mobile && (isMobile() || !desktop)) { return mobile; }
    return desktop || mobile || '';
  }

  /* Src is chosen ONCE, at attach time, and never swapped on resize --
     re-pointing a playing video at a different file guarantees the
     black reload flash this whole file exists to remove. */
  function attach(v) {
    if (v._frmAttached) { return; }
    var src = chooseSrc(v);
    if (!src) { return; }
    v._frmAttached = true;
    /* muted has to be set as a PROPERTY. The attribute alone is not
       enough -- a browser that restored a previous unmuted state will
       refuse to autoplay and give no error worth reading. */
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('preload', 'metadata');
    v.src = src;
    trimPool();
  }

  function release(v) {
    if (!v._frmAttached || v._frmVisible) { return; }
    v._frmAttached = false;
    v._frmLast = -1;
    v._frmSame = 0;
    try {
      v.removeAttribute('src');
      /* load() is what actually hands the resource back. Clearing src
         alone leaves the element holding it. */
      v.load();
    } catch (e) {}
  }

  function trimPool() {
    var attached = [];
    for (var i = 0; i < videos.length; i++) {
      if (videos[i]._frmAttached) { attached.push(videos[i]); }
    }
    var over = attached.length - MAX_ATTACHED;
    if (over <= 0) { return; }

    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var mid = vh / 2;
    var dist = function (v) {
      var r = v.getBoundingClientRect();
      if (!r.width && !r.height) { return 0; }
      return Math.abs((r.top + r.bottom) / 2 - mid);
    };

    var idle = [];
    for (var j = 0; j < attached.length; j++) {
      if (!attached[j]._frmVisible) { idle.push(attached[j]); }
    }
    idle.sort(function (a, b) { return dist(b) - dist(a); });
    for (var k = 0; k < idle.length && k < over; k++) { release(idle[k]); }
  }

  function play(v) {
    if (!v._frmVisible) { return; }
    attach(v);
    if (!v._frmAttached) { return; }
    v.muted = true;
    var p;
    try { p = v.play(); } catch (e) { return; }
    /* A rejected play() is the normal state under Low Power Mode or a
       strict autoplay policy. Swallow it: the gesture handler retries. */
    if (p && typeof p.catch === 'function') { p.catch(function () {}); }
  }

  function pause(v) {
    v._frmPaused = true;
    try { if (!v.paused) { v.pause(); } } catch (e) {}
    v._frmPaused = false;
  }

  function tick() {
    var live = 0;
    for (var i = 0; i < videos.length; i++) {
      var v = videos[i];
      if (!v._frmVisible || !v._frmAttached) { v._frmLast = -1; v._frmSame = 0; continue; }
      live++;
      if (v.paused || v.ended) { play(v); continue; }
      if (v.seeking || v.readyState < 2) { continue; }
      if (v.currentTime === v._frmLast) {
        v._frmSame = (v._frmSame || 0) + 1;
        if (v._frmSame >= STALL_TICKS) { v._frmSame = 0; play(v); }
      } else {
        v._frmLast = v.currentTime;
        v._frmSame = 0;
      }
    }
    /* Nothing on screen -- stop burning a timer until something is. */
    if (!live && timer) { clearInterval(timer); timer = null; }
  }

  function startTimer() {
    if (!timer) { timer = setInterval(tick, TICK_MS); }
  }

  function setVisible(v, on) {
    v._frmVisible = !!on;
    if (on) { startTimer(); play(v); } else { pause(v); }
  }

  /* ---- FAILSAFE: the same decision, from rect maths ----
     For no IntersectionObserver at all, and for one that never
     reports. Deciding by rect keeps the pool -- the point of the whole
     exercise -- rather than playing everything at once, which is the
     very thing that put black boxes on the phone. A degenerate rect
     (all zeros: the element sits in a container that has not been laid
     out) counts as visible, because the one outcome never worth
     risking is a row that stays blank. */
  function manualPass() {
    manualPending = false;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    for (var i = 0; i < videos.length; i++) {
      var v = videos[i];
      var r = v.getBoundingClientRect();
      var degenerate = !r.width && !r.height;
      if (degenerate || (r.bottom > -400 && r.top < vh + 400)) { attach(v); }
      var on = degenerate || (r.bottom > 0 && r.top < vh);
      if (!v._frmVisible !== !on) { setVisible(v, on); }
      else if (on) { play(v); }
    }
  }

  function queueManualPass() {
    if (manualPending) { return; }
    manualPending = true;
    setTimeout(manualPass, 100);
  }

  function startManual() {
    /* Already running: a newly wired row still needs its first pass,
       or it is the only one left blank. */
    if (manual) { manualPass(); return; }
    manual = true;
    window.addEventListener('scroll', queueManualPass, { passive: true });
    window.addEventListener('resize', queueManualPass, { passive: true });
    manualPass();
  }

  /* A hidden tab is not a failure -- it reports nothing on purpose and
     should decode nothing -- so only judge the observer while visible. */
  function checkObserverAlive() {
    if (sawCallback || manual) { return; }
    if (document.visibilityState === 'hidden') { return; }
    startManual();
  }

  function makeObservers() {
    if (!('IntersectionObserver' in window)) { return false; }
    if (prepareIO) { return true; }

    /* Deliberately NOT unobserved once attached: the pool may have
       taken this row's source back while it was far away, and it has
       to be re-attached when it comes near again. attach() is
       idempotent, so repeat callbacks cost nothing. */
    prepareIO = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { attach(entries[i].target); }
      }
    }, { rootMargin: PREPARE_MARGIN, threshold: 0 });

    /* On screen: play. Off screen: pause and hand the decoder back. */
    playIO = new IntersectionObserver(function (entries) {
      sawCallback = true;
      for (var i = 0; i < entries.length; i++) {
        setVisible(entries[i].target, entries[i].isIntersecting);
      }
    }, { threshold: 0.01 });

    return true;
  }

  function wire(v) {
    if (v._frmWired) { return; }
    v._frmWired = true;
    v._frmVisible = false;
    v._frmLast = -1;
    v._frmSame = 0;
    videos.push(v);

    /* iOS can sit on the final frame rather than loop. */
    v.addEventListener('ended', function () {
      if (!v._frmVisible) { return; }
      try { v.currentTime = 0; } catch (e) {}
      play(v);
    });

    /* Any pause we did not ask for -- an OS interruption, a decoder
       taken away -- is one to undo. */
    v.addEventListener('pause', function () {
      if (v._frmPaused || !v._frmVisible) { return; }
      play(v);
    });

    v.addEventListener('stalled', function () { play(v); });
    v.addEventListener('suspend', function () { if (v._frmVisible) { play(v); } });
    v.addEventListener('loadedmetadata', function () { play(v); });

    if (makeObservers()) {
      prepareIO.observe(v);
      playIO.observe(v);
      setTimeout(checkObserverAlive, 1500);
    } else {
      startManual();
    }
  }

  function scan(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var found = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < found.length; i++) { wire(found[i]); }
    return found.length;
  }

  /* A tab coming back to the foreground: iOS leaves every video paused. */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') { return; }
    if (manual) { manualPass(); }
    setTimeout(checkObserverAlive, 1500);
    startTimer();
    for (var i = 0; i < videos.length; i++) { play(videos[i]); }
  });

  window.addEventListener('pageshow', function () {
    for (var i = 0; i < videos.length; i++) { play(videos[i]); }
  });

  /* Low Power Mode and strict autoplay policies refuse play() until a
     real user gesture has happened. Retry inside the first one. */
  function unlock() {
    for (var i = 0; i < videos.length; i++) { play(videos[i]); }
  }
  ['touchstart', 'pointerdown', 'click', 'keydown'].forEach(function (type) {
    document.addEventListener(type, unlock, { once: true, capture: true, passive: true });
  });

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  window.PgcFeatureRowMedia = {
    version: 1,
    scan: scan,
    /* Exposed for debugging in the console: PgcFeatureRowMedia.state() */
    state: function () {
      return {
        rows: videos.length,
        attached: videos.filter(function (v) { return v._frmAttached; }).length,
        playing: videos.filter(function (v) { return !v.paused; }).length,
        maxAttached: MAX_ATTACHED,
        mode: manual ? 'rect failsafe' : 'IntersectionObserver'
      };
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
})();
